/**
 * EscopoContagemPage v2 — Simplified 2-tab layout
 *
 * Tab 1: Vínculo Hospital/Auditor (global, not month-dependent)
 * Tab 2: Escopo de Agendamento (monthly)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { CalendarDays, Link2, ClipboardList, ShieldAlert } from 'lucide-react';
import escopoContagemService, { type EscopoMes } from '../services/escopoContagemService';
import { msg } from '../utils/messages';
import VinculosAuditoresTab from '../components/escopo/VinculosAuditoresTab';
import AgendamentoTab from '../components/escopo/AgendamentoTab';
import ForaDoEscopoTab from '../components/escopo/ForaDoEscopoTab';
import DashboardEscopo from '../components/escopo/DashboardEscopo';
import PermissionGate from '../components/PermissionGate';
import { useQueryClient } from '@tanstack/react-query';
import useAuthStore from '../stores/authStore';

type EscopoTabType = 'vinculos' | 'agendamento' | 'fora_escopo';

const TABS: { id: EscopoTabType; label: string; icon: any }[] = [
  { id: 'vinculos', label: 'Vínculo Hospital/Auditor', icon: Link2 },
  { id: 'agendamento', label: 'Escopo de Agendamento', icon: ClipboardList },
  { id: 'fora_escopo', label: 'Fora do Escopo', icon: ShieldAlert },
];

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function getDefaultMonth() {
  const now = new Date();
  let m = now.getMonth() + 2;
  let y = now.getFullYear();
  if (m > 12) { m = 1; y += 1; }
  return { mes: m, ano: y };
}

function lastDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

export default function EscopoContagemPage() {
  const user = useAuthStore(state => state.user);
  const isMaster = user?.role === 'MASTER';
  const [activeTab, setActiveTab] = useState<EscopoTabType>(
    () => (user?.role === 'MASTER' ? 'vinculos' : 'agendamento'),
  );
  const defaultDate = getDefaultMonth();
  const [mesSel, setMesSel] = useState(defaultDate.mes);
  const [anoSel, setAnoSel] = useState(defaultDate.ano);
  const [escopo, setEscopo] = useState<EscopoMes | null>(null);
  const [loading, setLoading] = useState(false);
  const [dashboardKey, setDashboardKey] = useState(0);
  const queryClient = useQueryClient();

  // Qualquer acao numa aba (agendar/editar/remover/incluir/upload) atualiza:
  // (a) Dashboard + abas-irmas via dashboardKey/refreshKey e
  // (b) o Calendario (React Query ['jobs']) — sem clicar "Atualizar".
  const handleDataChanged = useCallback(() => {
    setDashboardKey(k => k + 1);
    queryClient.invalidateQueries({ queryKey: ['jobs'], refetchType: 'all' });
  }, [queryClient]);

  const loadEscopo = useCallback(async () => {
    setLoading(true);
    try {
      const escopos = await escopoContagemService.listarEscopos();
      const found = escopos.find(e => e.mes === mesSel && e.ano === anoSel);
      setEscopo(found || null);
    } catch {
      setEscopo(null);
    } finally {
      setLoading(false);
    }
  }, [mesSel, anoSel]);

  useEffect(() => {
    loadEscopo();
  }, [loadEscopo]);

  useEffect(() => {
    if (!isMaster && activeTab !== 'agendamento') {
      setActiveTab('agendamento');
    }
  }, [activeTab, isMaster]);

  const visibleTabs = isMaster
    ? TABS
    : TABS.filter(tab => tab.id === 'agendamento');

  const handleCreateEscopo = async () => {
    try {
      const dataInicio = `${anoSel}-${String(mesSel).padStart(2, '0')}-01`;
      const lastDay = lastDayOfMonth(anoSel, mesSel);
      const dataFim = `${anoSel}-${String(mesSel).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const novo = await escopoContagemService.criarEscopo({
        mes: mesSel, ano: anoSel,
        data_inicio: dataInicio, data_fim: dataFim,
      });
      setEscopo(novo);
      msg.success('Escopo criado com sucesso!');
    } catch (err: any) {
      msg.error(err?.response?.data?.detail || 'Erro ao criar escopo');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pl-16 lg:pl-56">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Escopo de Contagem
            </h1>
          </div>

          {/* Month/Year selectors */}
          <div className="flex items-center gap-2">
            <select
              value={mesSel}
              onChange={e => setMesSel(Number(e.target.value))}
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
            >
              {MESES.map((nome, idx) => (
                <option key={idx} value={idx + 1}>{nome}</option>
              ))}
            </select>
            <select
              value={anoSel}
              onChange={e => setAnoSel(Number(e.target.value))}
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
            >
              {[2025, 2026, 2027].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Dashboard KPIs (above tabs, auto-loads when escopo exists) */}
        {escopo && (
          <DashboardEscopo escopoId={escopo.id} mes={mesSel} ano={anoSel} refreshKey={dashboardKey} />
        )}

        {/* Tab bar */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 mb-4">
          <div className="flex px-4 overflow-x-auto">
            {visibleTabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                    isActive
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab content */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6">
          {isMaster && activeTab === 'vinculos' && (
            <VinculosAuditoresTab />
          )}

          {activeTab === 'agendamento' && (
            <>
              {loading && (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}

              {!loading && !escopo && (
                <div className="text-center py-12">
                  <CalendarDays className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Nenhum escopo para {MESES[mesSel - 1]} {anoSel}
                  </p>
                  <PermissionGate tela="ESCOPO_CONTAGEM_VIEW" acao="criar">
                    <button
                      onClick={handleCreateEscopo}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      Criar Escopo para {MESES[mesSel - 1]} {anoSel}
                    </button>
                  </PermissionGate>
                </div>
              )}

              {!loading && escopo && (
                <AgendamentoTab escopo={escopo} refreshKey={dashboardKey} onDataChanged={handleDataChanged} />
              )}
            </>
          )}

          {isMaster && activeTab === 'fora_escopo' && (
            <>
              {loading && (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}

              {!loading && !escopo && (
                <div className="text-center py-12">
                  <CalendarDays className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Nenhum escopo para {MESES[mesSel - 1]} {anoSel}
                  </p>
                  <PermissionGate tela="ESCOPO_CONTAGEM_VIEW" acao="criar">
                    <button
                      onClick={handleCreateEscopo}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      Criar Escopo para {MESES[mesSel - 1]} {anoSel}
                    </button>
                  </PermissionGate>
                </div>
              )}

              {!loading && escopo && (
                <ForaDoEscopoTab escopo={escopo} refreshKey={dashboardKey} onDataChanged={handleDataChanged} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
